import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { SelectModule } from 'primeng/select';
import { CheckboxModule } from 'primeng/checkbox';
import { InputNumberModule } from 'primeng/inputnumber';
import { AppButtonComponent } from '@app/shared/components/app-button/app-button.component';
import { PaginatedSelectComponent } from '@shared/components/paginated-select/paginated-select.component';
import {
  mergePaginatedOptions,
  PaginatedDropdownConfig,
  PaginatedOption,
} from '@shared/utils/paginated-dropdown.util';
import { SkuOptionsService } from '@shared/services/sku-options.service';
import { Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { signal } from '@angular/core';
import { extractErrorMessage } from '@app/shared/utils/error-handler.util';

import {
  VENDOR_SKU_FORM_FIELDS,
  VendorSkuFormField,
} from '../../vendor-manage-sku.config';
import {
  resolveVendorSkuErrorKey,
  VENDOR_SKU_DUPLICATE_MESSAGE_KEY,
} from '../../vendor-sku-error.util';

export interface VendorSkuFormData {
  skuCode?: string | null;
  skuName?: string;
  vendorSkuCode: string;
  vendorProductName: string;
  description?: string;
  active: boolean;
  primaryFlag?: boolean;
  unitPrice: number;
}

@Component({
  selector: 'app-vendor-sku-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    DialogModule,
    ButtonModule,
    InputTextModule,
    TextareaModule,
    SelectModule,
    CheckboxModule,
    InputNumberModule,
    AppButtonComponent,
    PaginatedSelectComponent,
  ],
  templateUrl: './vendor-sku-dialog.component.html',
  styleUrl: './vendor-sku-dialog.component.css',
})
export class VendorSkuDialogComponent implements OnChanges, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly translateService = inject(TranslateService);
  private readonly skuOptionsService = inject(SkuOptionsService);
  private readonly destroy$ = new Subject<void>();
  private loadedSkuOptions: PaginatedOption<string>[] = [];

  @Input({ required: true }) visible!: boolean;
  @Input({ required: true }) editMode!: boolean;
  @Input() initialData?: VendorSkuFormData | null;
  @Input({ required: true }) loading!: boolean;

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() confirm = new EventEmitter<VendorSkuFormData>();
  @Output() cancel = new EventEmitter<void>();

  form: FormGroup;
  formFields = VENDOR_SKU_FORM_FIELDS;
  errorMessage = signal<string | null>(null);
  readonly skuPaginatedConfig: PaginatedDropdownConfig = {
    pageSize: 20,
    debounceMs: 300,
    loadPage: (params) =>
      this.skuOptionsService
        .getOptionsPage(params, { searchBy: 'skuCode' })
        .pipe(
          map((page) => {
            const options = page.options.map((option) => ({
              label: option.label,
              value: String(option.value),
            }));
            this.loadedSkuOptions = mergePaginatedOptions(
              this.loadedSkuOptions,
              options,
            );
            return { ...page, options };
          }),
        ),
  };

  constructor() {
    this.form = this.fb.group({
      skuCode: [null, [Validators.required]],
      vendorSkuCode: ['', [Validators.required, Validators.minLength(1)]],
      vendorProductName: ['', [Validators.required, Validators.minLength(1)]],
      description: [''],
      unitPrice: [null, [Validators.required, Validators.min(1)]],
      active: [true],
      primaryFlag: [false],
    });

    this.form.valueChanges.subscribe(() => {
      this.errorMessage.set(null);
    });
  }

  get dialogTitle(): string {
    return this.editMode
      ? this.translateService.instant('vendor.manage_sku.edit_sku_dialog.title')
      : this.translateService.instant('vendor.manage_sku.add_sku_dialog.title');
  }

  get confirmButtonLabel(): string {
    return this.translateService.instant('common.actions.save');
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible) {
      this.resetForm();
      if (this.initialData && this.editMode) {
        setTimeout(() => this.patchFormData(), 0);
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private patchFormData(): void {
    if (!this.initialData) return;

    this.form.patchValue(
      {
        skuCode: this.initialData.skuCode || null,
        vendorSkuCode: this.initialData.vendorSkuCode || '',
        vendorProductName: this.initialData.vendorProductName || '',
        description: this.initialData.description || '',
        unitPrice: this.initialData.unitPrice ?? null,
        active: this.initialData.active ?? true,
        primaryFlag: this.initialData.primaryFlag ?? false,
      },
      { emitEvent: false },
    );

    if (this.editMode) {
      this.disableControl('skuCode');
      this.disableControl('vendorSkuCode');
      this.enableControl('vendorProductName', [
        Validators.required,
        Validators.minLength(1),
      ]);
      this.enableControl('description');
      this.enableControl('unitPrice', [Validators.required, Validators.min(1)]);
      this.enableControl('active');
      this.enableControl('primaryFlag');
    }
  }

  private disableControl(name: string): void {
    const control = this.form.get(name);
    control?.disable({ emitEvent: false });
    control?.clearValidators();
    control?.updateValueAndValidity({ emitEvent: false });
  }

  private enableControl(name: string, validators?: any[]): void {
    const control = this.form.get(name);
    control?.enable({ emitEvent: false });
    if (validators) {
      control?.setValidators(validators);
    }
    control?.updateValueAndValidity({ emitEvent: false });
  }

  onDialogHide(): void {
    this.visibleChange.emit(false);
    this.cancel.emit();
    this.resetForm();
  }

  onConfirm(): void {
    if (this.form.valid) {
      this.errorMessage.set(null);
      const value = this.form.getRawValue();
      this.confirm.emit({
        skuCode: value.skuCode,
        skuName: this.loadedSkuOptions.find(
          (option) => option.value === value.skuCode,
        )?.label,
        vendorSkuCode: value.vendorSkuCode,
        vendorProductName: value.vendorProductName,
        description: value.description || '',
        unitPrice: Number(value.unitPrice) || 0,
        active: value.active ?? true,
        primaryFlag: value.primaryFlag ?? false,
      });
    }
  }

  onCancel(): void {
    this.visibleChange.emit(false);
    this.cancel.emit();
    this.resetForm();
  }

  setError(error: any): void {
    const messageKey = resolveVendorSkuErrorKey(error);
    if (!messageKey) {
      this.errorMessage.set(extractErrorMessage(error));
      return;
    }

    if (messageKey === VENDOR_SKU_DUPLICATE_MESSAGE_KEY) {
      const control = this.form.get('vendorSkuCode');
      control?.setErrors({ duplicated: true });
      control?.markAsTouched();
    }
    this.errorMessage.set(this.translateService.instant(messageKey));
  }

  private resetForm(): void {
    this.errorMessage.set(null);
    this.form.reset(
      {
        skuCode: null,
        vendorSkuCode: '',
        vendorProductName: '',
        description: '',
        unitPrice: null,
        active: true,
        primaryFlag: false,
      },
      { emitEvent: false },
    );
    [
      'skuCode',
      'vendorSkuCode',
      'vendorProductName',
      'description',
      'unitPrice',
      'active',
      'primaryFlag',
    ].forEach((name) => this.form.get(name)?.enable({ emitEvent: false }));
  }

  getFieldError(fieldKey: string): string | null {
    const control = this.form.get(fieldKey);
    if (control?.hasError('duplicated')) {
      return this.translateService.instant(VENDOR_SKU_DUPLICATE_MESSAGE_KEY);
    }
    if (control?.hasError('required') && control?.touched) {
      return this.translateService.instant(
        `vendor.messages.error.${fieldKey}_required`,
      );
    }
    if (control?.hasError('min') && control?.touched) {
      return this.translateService.instant(
        'vendor.messages.error.unit_price_min',
      );
    }
    if (control?.hasError('minlength') && control?.touched) {
      return this.translateService.instant('vendor.messages.error.min_length');
    }
    return null;
  }

  getFieldConfig(key: string): VendorSkuFormField | undefined {
    return this.formFields.find((field) => field.key === key);
  }
}
